import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  BulkConceptSubtreeRequest,
  BulkConceptsAncestorsResponse,
  ConceptScheme,
  DescendantsConceptsResponse,
  ExpandConceptRequest,
  ExpandConceptResponse,
  KnowledgeGraphManagerApi,
  NodesChildrenRequest,
  NodesResolveRequest,
  NodesResponse,
  NodesSearchRequest,
  NodesSearchResponse,
  RelatedConceptsRequest,
  RelatedConceptsResponse,
  SearchRequest,
  SearchResult,
} from '@agilelab/plugin-wb-marketplace-common';

/**
 * Client implementation for Knowledge Graph Manager API
 * @public
 */
export class KnowledgeGraphManagerClient implements KnowledgeGraphManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('marketplace');
  }

  async getConceptSchemes(): Promise<ConceptScheme[]> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/knowledge-graph/concept-schemes`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async expandConcept(
    request: ExpandConceptRequest,
  ): Promise<ExpandConceptResponse> {
    const baseUrl = await this.getBaseUrl();
    const conceptId = encodeURIComponent(request.conceptId);
    const params = new URLSearchParams();

    if (request.limit !== undefined) {
      params.append('limit', request.limit.toString());
    }
    if (request.offset !== undefined) {
      params.append('offset', request.offset.toString());
    }

    const url = `${baseUrl}/knowledge-graph/concepts/${conceptId}/expand?${params.toString()}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getRelatedConcepts(
    request: RelatedConceptsRequest,
  ): Promise<RelatedConceptsResponse> {
    const baseUrl = await this.getBaseUrl();
    const conceptId = encodeURIComponent(request.conceptId);
    const params = new URLSearchParams();

    if (request.limit !== undefined) {
      params.append('limit', request.limit.toString());
    }
    if (request.offset !== undefined) {
      params.append('offset', request.offset.toString());
    }

    const url = `${baseUrl}/knowledge-graph/concepts/${conceptId}/related?${params.toString()}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getBulkConceptsAncestors(
    request: BulkConceptSubtreeRequest,
  ): Promise<BulkConceptsAncestorsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/knowledge-graph/concepts/ancestors`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async search(request: SearchRequest): Promise<SearchResult> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({
      term: request.term,
    });

    if (request.limit !== undefined) {
      params.append('limit', request.limit.toString());
    }

    if (request.offset !== undefined) {
      params.append('offset', request.offset.toString());
    }

    const url = `${baseUrl}/knowledge-graph/search?${params.toString()}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getConceptDescendants(
    conceptId: string,
  ): Promise<DescendantsConceptsResponse> {
    const baseUrl = await this.getBaseUrl();
    const encodedConceptId = encodeURIComponent(conceptId);
    const url = `${baseUrl}/knowledge-graph/concepts/${encodedConceptId}/descendants`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async resolveNodes(request: NodesResolveRequest): Promise<NodesResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/knowledge-graph/v1/nodes/resolve`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getChildren(request: NodesChildrenRequest): Promise<NodesResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/knowledge-graph/v1/nodes/children`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async searchNodes(request: NodesSearchRequest): Promise<NodesSearchResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/knowledge-graph/v1/nodes/search`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }
}
